import * as Phaser from "phaser";
import { BasicScene } from "$/game/prefabs/scenes/BasicScene.ts";
import { EmojiFlyEffect } from "$/game/prefabs/effects/EmojiFlyEffect.ts";
import { petActions } from "$/game/emitters.ts";

export enum PetGameState {
  Idle,
  Moving,
  Eating,
  Sleeping,
  Playing,
  Cleaning,
  Dead,
}

export class PetGameObject extends Phaser.Physics.Matter.Sprite {
  private petType: string = "";
  // поверхность по которой может двигаться питомец
  private surface: Phaser.GameObjects.Sprite;
  private gameState: PetGameState = PetGameState.Idle;
  private eventListeners: Phaser.Events.EventEmitter[] = [];

  // Система блуждания
  private wanderTimer?: Phaser.Time.TimerEvent;
  private currentTween?: Phaser.Tweens.Tween;
  private wobbleTween?: Phaser.Tweens.Tween;
  private isWandering: boolean = false;

  // Система сна
  private isSleeping: boolean = false;

  constructor(private myScene: BasicScene, surface: Phaser.GameObjects.Sprite) {
    super(
      myScene.matter.world,
      surface.x,
      surface.y,
      myScene.atlas,
      myScene.keys.assets.sprites.pets.shark.normal,
    );
    this.surface = surface;
    // вот тут текстура смещена относительно физического тела
    this.setOrigin(0.5, 1);
    // надо сместить тело на половину высоты текстуры
    this.setPosition(this.x, this.y - this.height / 2);
    this.setCircle(this.width / 2);
    this.setStatic(true);
    this.setInteractive({ useHandCursor: true });

    this.setupSprite();
    myScene.registry.events.on("changedata-pet", () => {
      if (this.scene !== null && this.active) {
        this.setupSprite();
      }
    });

    this.eventListeners.push(petActions.onSoapUsed(() => {
      this.showEmoji("🩵");
    }));

    this.eventListeners.push(petActions.onBallPlayed(() => {
      this.showEmoji("🤣");
    }));

    this.eventListeners.push(petActions.onPoopCleaned(() => {
      this.showEmoji("🧹");
    }));

    this.eventListeners.push(petActions.onPoopEmitted(() => {
      this.showEmoji("💩");
    }));

    this.eventListeners.push(petActions.onSleepStart(() => {
      this.startSleep();
    }));

    this.eventListeners.push(petActions.onSleepEnd(() => {
      this.endSleep();
    }));

    this.eventListeners.push(petActions.onFoodEaten(() => {
      this.showEmoji("😋");
    }));

    // Запускаем систему блуждания
    this.startWandering();
  }

  /**
   * Переводит питомца в режим сна
   */
  startSleep(): void {
    if (this.isSleeping) return;

    this.isSleeping = true;
    this.gameState = PetGameState.Sleeping;

    // Останавливаем блуждание
    this.stopWandering();
  }

  /**
   * Выводит питомца из режима сна
   */
  endSleep(): void {
    if (!this.isSleeping) return;

    this.isSleeping = false;
    this.gameState = PetGameState.Idle;

    // Возобновляем блуждание
    this.startWandering();
  }

  /**
   * Проверяет, спит ли питомец
   */
  getIsSleeping(): boolean {
    return this.isSleeping;
  }

  /**
   * Запускает систему случайного блуждания питомца
   */
  startWandering(): void {
    if (this.isWandering) return;
    this.isWandering = true;
    this.scheduleNextMovement();
  }

  /**
   * Останавливает систему блуждания
   */
  stopWandering(): void {
    this.isWandering = false;
    if (this.wanderTimer) {
      this.wanderTimer.destroy();
      this.wanderTimer = undefined;
    }
    if (this.currentTween) {
      this.currentTween.stop();
      this.currentTween = undefined;
    }
    this.stopWobble();
  }

  /**
   * Запускает покачивание спрайта при ходьбе
   */
  private startWobble(): void {
    if (this.wobbleTween) return;

    const config = this.myScene.global.TUNER_TWA_CONFIG.game.pet.animation;
    this.wobbleTween = this.myScene.tweens.add({
      targets: this,
      angle: { from: -config.wobbleAngle, to: config.wobbleAngle },
      duration: config.wobbleDuration,
      ease: "Sine.easeInOut",
      yoyo: true,
      repeat: -1, // бесконечное повторение
    });
  }

  /**
   * Останавливает покачивание и возвращает спрайт в нормальное положение
   */
  private stopWobble(): void {
    if (this.wobbleTween) {
      this.wobbleTween.stop();
      this.wobbleTween = undefined;
    }
    // Плавно возвращаем угол к 0
    this.myScene.tweens.add({
      targets: this,
      angle: 0,
      duration: 100,
      ease: "Sine.easeOut",
    });
  }

  /**
   * Планирует следующее перемещение с случайной задержкой
   */
  private scheduleNextMovement(): void {
    if (!this.isWandering || !this.scene) return;

    const config = this.myScene.global.TUNER_TWA_CONFIG.game.pet.wandering;
    const delay = Phaser.Math.Between(
      config.minDelay,
      config.maxDelay,
    );

    this.wanderTimer = this.myScene.time.delayedCall(delay, () => {
      this.moveToRandomPosition();
    });
  }

  /**
   * Перемещает питомца в случайную точку на поверхности
   */
  private moveToRandomPosition(): void {
    if (
      !this.isWandering || !this.scene || this.gameState !== PetGameState.Idle
    ) {
      this.scheduleNextMovement();
      return;
    }

    // Вычисляем границы surface (ковра)
    const surfaceBounds = this.surface.getBounds();
    const padding = this.width / 2; // отступ от краёв, чтобы питомец не выходил за границы

    const minX = surfaceBounds.left + padding;
    const maxX = surfaceBounds.right - padding;

    // Выбираем случайную точку X в пределах surface
    const targetX = Phaser.Math.Between(minX, maxX);
    const targetY = this.surface.y - this.height / 2; // Y остаётся на уровне surface

    // Определяем направление для отзеркаливания спрайта
    if (targetX < this.x) {
      this.setFlipX(true); // движение влево
    } else if (targetX > this.x) {
      this.setFlipX(false); // движение вправо
    }

    // Вычисляем время перемещения на основе расстояния
    const config = this.myScene.global.TUNER_TWA_CONFIG.game.pet.wandering;
    const distance = Math.abs(targetX - this.x);
    const duration = (distance / config.moveSpeed) * 1000;

    // Меняем состояние на Moving
    this.gameState = PetGameState.Moving;

    // Запускаем покачивание
    this.startWobble();

    // Создаём tween для плавного перемещения
    this.currentTween = this.myScene.tweens.add({
      targets: this,
      x: targetX,
      y: targetY,
      duration: duration,
      ease: "Sine.easeInOut",
      onComplete: () => {
        this.stopWobble();
        this.gameState = PetGameState.Idle;
        this.scheduleNextMovement();
      },
    });
  }

  /**
   * Получить текущее состояние питомца
   */
  getGameState(): PetGameState {
    return this.gameState;
  }

  /**
   * Установить состояние питомца (для внешних систем)
   */
  setGameState(state: PetGameState): void {
    this.gameState = state;
  }

  showEmoji(emoji: string) {
    const frameKey = this.mapEmojiToAsset(emoji);
    const config = this.myScene.global.TUNER_TWA_CONFIG.game.effects.emoji;
    this.myScene.add.existing(
      new EmojiFlyEffect(
        this.myScene,
        this.x,
        this.y - this.height / 2,
        this.myScene.atlas,
        frameKey,
        config.flyDuration,
      ),
    );
  }

  private mapEmojiToAsset(emoji: string): string {
    // Map emojis to corresponding asset keys
    const emojiToAssetMap: Record<string, string> = {
      "🩵": this.myScene.keys.assets.sprites.emoji.emojiWash,
      "🤣": this.myScene.keys.assets.sprites.emoji.emojiPlay,
      "🧹": this.myScene.keys.assets.sprites.emoji.emojiClean,
      "💩": this.myScene.keys.assets.sprites.emoji.emojiClean, // Using clean emoji for poop cleaning
      "💤": this.myScene.keys.assets.sprites.emoji.emojiSleep,
      "😋": this.myScene.keys.assets.sprites.emoji.emojiEat,
    };

    // Return the mapped asset key or a default one if not found
    return emojiToAssetMap[emoji] ||
      this.myScene.keys.assets.sprites.emoji.emojiPlay;
  }

  private setupSprite() {
    if (!this.myScene || !this.myScene.keys || !this.myScene.keys.assets) {
      console.warn("Scene or keys not available for PetGameObject setupSprite");
      return;
    }

    const pet = this.myScene.registry.get("pet");
    this.petType = pet?.type ?? "";

    if (!this.petType) {
      console.warn("Pet type is not defined");
      return;
    }

    const petSprites = this.myScene.keys.assets.sprites.pets;
    if (!(this.petType in petSprites)) {
      console.warn("Pet type not found in sprites:", this.petType);
      return;
    }

    const petTypeSprites = petSprites[this.petType as keyof typeof petSprites];
    if (!petTypeSprites) {
      console.warn("Pet sprites not available for type:", this.petType);
      return;
    }

    const isAlive = pet?.is_alive ?? false;
    const mood = pet?.mood ?? 0;
    const energy = pet?.energy ?? 0;
    const clean = pet?.clean ?? 0;
    const satiety = pet?.satiety ?? 0;

    try {
      let frameKey: string;

      if (isAlive) {
        if (mood < 20 || energy < 20 || clean < 20 || satiety < 20) {
          frameKey = petTypeSprites.sad;
        } else {
          frameKey = petTypeSprites.normal;
        }
      } else {
        frameKey = petTypeSprites.dead;
      }

      // Set texture with atlas and frame
      this.setTexture(this.myScene.atlas, frameKey);
    } catch (error) {
      console.error("Error setting pet texture:", error);
    }
  }

  override destroy(): void {
    this.stopWandering();
    this.eventListeners.forEach((listener) => listener.destroy());
    super.destroy();
  }
}
